import { ReactNode, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useGrafanaDashboard } from '../hooks/useGrafanaDashboard';

interface GrafanaPanelProps {
  dashboardUid: string;
  panelId: number;
  /**
   * Grafana template variables. `undefined` means "context not ready" and disables the
   * fetch entirely — it is NOT the same as an empty object, which would render the
   * dashboard's saved defaults (a cross-environment read for any datasource variable).
   */
  variables: Record<string, string | number> | undefined;
  width?: number;
  height?: number;
  from?: string;
  to?: string;
  retry?: number;
  retryDelay?: number;
  alt: string;
  /** Shown instead of the panel when `variables` is undefined. */
  missingContext: ReactNode;
  /** Shown when the render succeeds but produces no image. */
  empty: ReactNode;
  /** Overrides the default error line — a slow panel usually wants a deep link instead. */
  renderError?: (message: string) => ReactNode;
  /** Applies to the rendered panel, not the spinner — see LOADING_MIN_HEIGHT. */
  minHeight?: string;
  children?: ReactNode;
}

/**
 * A server-rendered Grafana panel.
 *
 * Owns the object-URL lifecycle and the loading/missing-context/error/empty ladder so each
 * embed does not re-derive it. The URL is created in an effect rather than during render
 * so it is only allocated for committed renders — under StrictMode a render-time
 * createObjectURL leaks one URL per discarded render.
 */
/**
 * Fixed, and deliberately not `minHeight`: the caller's height describes the panel it
 * expects, and letting a tall panel stretch the spinner box just moves the rest of the
 * page around while it loads.
 */
const LOADING_MIN_HEIGHT = '400px';

export const GrafanaPanel = ({
  dashboardUid,
  panelId,
  variables,
  width,
  height,
  from,
  to,
  retry,
  retryDelay,
  alt,
  missingContext,
  empty,
  renderError,
  minHeight,
  children,
}: GrafanaPanelProps) => {
  const {
    data: imageBlob,
    isLoading,
    error,
  } = useGrafanaDashboard({
    dashboardUid,
    panelId,
    variables,
    width,
    height,
    from,
    to,
    retry,
    retryDelay,
  });

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!imageBlob) {
      setImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageBlob);
    setImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageBlob]);

  if (!variables) {
    return <Box p={3}>{missingContext}</Box>;
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={LOADING_MIN_HEIGHT}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // A renderer timeout comes back as a 500 whose body is a Grafana error page, so the
    // upstream message is never useful on its own — callers that know what their panel
    // costs pass `renderError` to say something actionable instead.
    const anyError = error as any;
    const message =
      anyError?.data?.message ??
      anyError?.message ??
      (anyError?.status ? `Request failed with status ${anyError.status}` : 'Unknown error');

    return (
      <Box p={3}>
        {renderError ? renderError(message) : <Typography color="error">{message}</Typography>}
      </Box>
    );
  }

  if (!imageUrl) {
    return <Box p={3}>{empty}</Box>;
  }

  return (
    <Box sx={{ minHeight, mt: 2 }}>
      <img src={imageUrl} alt={alt} width="100%" style={{ display: 'block' }} />
      {children}
    </Box>
  );
};

export default GrafanaPanel;
